import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TrendingUp, DollarSign, AlertCircle, BarChart3, Activity, Target, ArrowLeft, Building2, Bitcoin, Shield } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Card } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyDealAnalysis = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  
  // Property data state
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Simulation Parameters
  const [ltv, setLtv] = useState(65);
  const [bitcoinCollateralPct, setBitcoinCollateralPct] = useState(37);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTermYears, setLoanTermYears] = useState(3);
  const [defaultProbability, setDefaultProbability] = useState(10);
  const [btcExpectedReturn, setBtcExpectedReturn] = useState(25);
  const [btcVolatility, setBtcVolatility] = useState(65);
  const [propertyExpectedReturn, setPropertyExpectedReturn] = useState(8);
  const [propertyVolatility, setPropertyVolatility] = useState(20);
  const [numSimulations, setNumSimulations] = useState(1000);
  const [aggregateHaircut, setAggregateHaircut] = useState(55);
  
  // Results
  const [isSimulating, setIsSimulating] = useState(false);
  const [results, setResults] = useState(null);
  const [btcPrice, setBtcPrice] = useState(94707);

  useEffect(() => {
    fetchBtcPrice();
    // Check if propertyId is a dashboard property ID (CRE-XXXX format)
    if (propertyId.startsWith('CRE-')) {
      loadDashboardProperty(propertyId);
    } else {
      fetchProperty();
    }
  }, [propertyId]);

  const fetchBtcPrice = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/crypto/prices`);
      const data = await response.json();
      if (data.success) {
        setBtcPrice(data.prices.BTC.price);
      }
    } catch (error) {
      console.error('Error fetching BTC price:', error);
    }
  };

  const fetchProperty = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cre/cre-properties/${propertyId}`);
      const data = await response.json();
      if (data.success) {
        setProperty(data.property);
      }
    } catch (error) {
      console.error('Error fetching property:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dashboard properties data
  const loadDashboardProperty = (id) => {
    const dashboardProperties = {
      'CRE-7821': { name: "The Meridian Tower", address: "1425 Brickell Ave, Miami, FL 33131", type: "Office", loanBalance: 48200000, dscr: 0.74, occupancy: 38, riskScore: 82, credIQScore: "D+", discount: 34, btcLendingRate: 8.4, assumableBalance: 31500000, currentLTV: 94, maturityDate: "2025-09-15", status: "DSCR < 1.0" },
      'CRE-4402': { name: "Westgate Commerce Park", address: "8800 NW Loop 410, San Antonio, TX 78245", type: "Retail", loanBalance: 22750000, dscr: 0.61, occupancy: 51, riskScore: 91, credIQScore: "D", discount: 28, btcLendingRate: 9.1, assumableBalance: 17200000, currentLTV: 88, maturityDate: "2025-06-01", status: "90+ Days Late" },
      'CRE-9134': { name: "Harbor Point Apartments", address: "330 Harbor Blvd, Weehawken, NJ 07086", type: "Multifamily", loanBalance: 67400000, dscr: 0.88, occupancy: 72, riskScore: 67, credIQScore: "C-", discount: 19, btcLendingRate: 7.6, assumableBalance: 44000000, currentLTV: 86, maturityDate: "2026-01-20", status: "Special Servicing" },
      'CRE-3307': { name: "Pinnacle Business Center", address: "2200 Concord Pike, Wilmington, DE 19803", type: "Office", loanBalance: 15600000, dscr: 0.52, occupancy: 29, riskScore: 88, credIQScore: "D-", discount: 41, btcLendingRate: 10.2, assumableBalance: 9800000, currentLTV: 97, maturityDate: "2024-11-30", status: "Maturity Default" },
      'CRE-6618': { name: "Sunridge Hotel & Suites", address: "4500 Airport Blvd, Austin, TX 78722", type: "Hospitality", loanBalance: 38900000, dscr: 0.83, occupancy: 58, riskScore: 74, credIQScore: "C", discount: 22, btcLendingRate: 8.8, assumableBalance: 26000000, currentLTV: 79, maturityDate: "2026-04-10", status: "DSCR < 1.0" },
      'CRE-1190': { name: "Lakeview Industrial Campus", address: "6100 Industrial Dr, Memphis, TN 38118", type: "Industrial", loanBalance: 29100000, dscr: 0.95, occupancy: 64, riskScore: 55, credIQScore: "C+", discount: 14, btcLendingRate: 7.2, assumableBalance: 19500000, currentLTV: 71, maturityDate: "2026-08-15", status: "Watchlist" },
    };
    
    const prop = dashboardProperties[id];
    if (prop) {
      // Calculate property value from LTV and loan
      const propertyValue = prop.loanBalance / (prop.currentLTV / 100);
      setProperty({
        id,
        name: prop.name,
        address: prop.address,
        property_type: prop.type,
        property_value: propertyValue,
        loan_amount: prop.loanBalance,
        ltv_ratio: prop.currentLTV,
        occupancy_rate: prop.occupancy,
        cap_rate: 6.5, // Estimated
        interest_rate: prop.btcLendingRate,
        is_distressed: true,
        status: prop.status,
        dscr: prop.dscr,
        riskScore: prop.riskScore,
        credIQScore: prop.credIQScore,
        discount: prop.discount,
        assumableBalance: prop.assumableBalance,
        maturityDate: prop.maturityDate
      });
      setLtv(prop.currentLTV);
      setInterestRate(prop.btcLendingRate);
      // Higher default prob for higher risk
      setDefaultProbability(Math.min(50, Math.round(prop.riskScore / 2)));
    }
    setLoading(false);
  };

  // Get property value
  const propertyValue = property?.property_value || 10000000;
  const loanAmount = propertyValue * (ltv / 100);
  const bitcoinCollateral = loanAmount * (bitcoinCollateralPct / 100);
  const btcRequired = bitcoinCollateral / btcPrice;

  // Monte Carlo Simulation
  const runMonteCarloSimulation = () => {
    setIsSimulating(true);
    
    setTimeout(() => {
      const simResults = performMonteCarloSimulation();
      setResults(simResults);
      setIsSimulating(false);
    }, 100);
  };

  const performMonteCarloSimulation = () => {
    const scenarios = [];
    let bankProfits = [];
    let assumeRateProfits = [];
    let defaultCount = 0;
    let bankLossCount = 0;
    let assumeRateLossCount = 0;
    let finalBtcPrices = [];
    let coverageRatios = [];

    for (let i = 0; i < numSimulations; i++) {
      const scenario = simulateScenario();
      scenarios.push(scenario);
      bankProfits.push(scenario.bankProfit);
      assumeRateProfits.push(scenario.assumeRateProfit);
      finalBtcPrices.push(scenario.finalBtcPrice);
      coverageRatios.push(scenario.coverageRatio);
      
      if (scenario.defaulted) defaultCount++;
      if (scenario.bankProfit < 0) bankLossCount++;
      if (scenario.assumeRateProfit < 0) assumeRateLossCount++;
    }

    const bankMeanProfit = mean(bankProfits);
    const bankMedianProfit = median(bankProfits);
    const bank5thPercentile = percentile(bankProfits, 5);
    const bank95thPercentile = percentile(bankProfits, 95);
    
    const assumeRateMeanProfit = mean(assumeRateProfits);
    const assumeRateMedianProfit = median(assumeRateProfits);
    const assumeRate5thPercentile = percentile(assumeRateProfits, 5);
    const assumeRate95thPercentile = percentile(assumeRateProfits, 95);
    
    const meanBtcPrice = mean(finalBtcPrices);
    const btcPriceChange = ((meanBtcPrice - btcPrice) / btcPrice) * 100;
    const meanCoverageRatio = mean(coverageRatios);

    return {
      scenarios,
      bank: {
        meanProfit: bankMeanProfit,
        medianProfit: bankMedianProfit,
        percentile5: bank5thPercentile,
        percentile95: bank95thPercentile,
        lossProbability: (bankLossCount / numSimulations) * 100,
        roi: (bankMeanProfit / loanAmount) * 100,
        meanRecovery: bankMeanProfit + loanAmount
      },
      assumeRate: {
        meanProfit: assumeRateMeanProfit,
        medianProfit: assumeRateMedianProfit,
        percentile5: assumeRate5thPercentile,
        percentile95: assumeRate95thPercentile,
        lossProbability: (assumeRateLossCount / numSimulations) * 100,
        meanRevenue: assumeRateMeanProfit + (propertyValue * 0.02)
      },
      defaultRate: (defaultCount / numSimulations) * 100,
      meanBtcPrice,
      btcPriceChange,
      meanCoverageRatio
    };
  };

  const simulateScenario = () => {
    const defaulted = Math.random() < (defaultProbability / 100);
    
    // Simulate Bitcoin price with geometric Brownian motion
    const dt = loanTermYears;
    const drift = (btcExpectedReturn / 100) * dt;
    const randomShock = (btcVolatility / 100) * Math.sqrt(dt) * randomNormal();
    const btcReturn = Math.exp(drift + randomShock);
    const finalBtcPrice = btcPrice * btcReturn;
    
    // Simulate property value
    const propertyDrift = (propertyExpectedReturn / 100) * dt;
    const propertyShock = (propertyVolatility / 100) * Math.sqrt(dt) * randomNormal();
    const propertyReturn = Math.exp(propertyDrift + propertyShock);
    const finalPropertyValue = propertyValue * propertyReturn;

    // Calculate collateral values
    const btcMarketValue = btcRequired * finalBtcPrice;
    const usableBtcCollateral = btcMarketValue * (1 - aggregateHaircut / 100);
    const reEquity = finalPropertyValue - loanAmount;
    const totalCollateral = usableBtcCollateral + Math.max(0, reEquity);
    const coverageRatio = (totalCollateral / loanAmount) * 100;

    if (defaulted) {
      const btcRecovery = btcRequired * finalBtcPrice;
      const propertyRecovery = finalPropertyValue * 0.85;
      const totalRecovery = btcRecovery + propertyRecovery;
      const bankProfit = totalRecovery - loanAmount - (loanAmount * 0.05);
      const assumeRateProfit = (propertyValue * 0.02) + (btcRecovery * 0.015) - (propertyValue * 0.003);
      
      return { defaulted: true, bankProfit, assumeRateProfit, finalBtcPrice, finalPropertyValue, coverageRatio };
    } else {
      const interestEarned = loanAmount * (interestRate / 100) * loanTermYears;
      const bankProfit = interestEarned;
      const assumeRateProfit = (propertyValue * 0.02) + (0.024 * 1000 * loanTermYears) - (propertyValue * 0.003);
      
      return { defaulted: false, bankProfit, assumeRateProfit, finalBtcPrice, finalPropertyValue, coverageRatio };
    }
  };

  const randomNormal = () => {
    let u = 0, v = 0;
    while(u === 0) u = Math.random();
    while(v === 0) v = Math.random();
    return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  };

  const mean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const median = (arr) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };
  const percentile = (arr, p) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const index = Math.floor((p / 100) * sorted.length);
    return sorted[index];
  };

  const fmt = (n) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : `$${(n / 1e3).toFixed(0)}K`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F7F7]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-[#6B6B76]">Loading property data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Navbar />
      
      <div className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Button 
              onClick={() => navigate(-1)}
              variant="outline"
              className="mb-4 border-[#006AFF] text-[#006AFF]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            <h1 className="text-3xl font-bold text-[#2A2A33] mb-2">
              Deal Flow Analysis: {property?.name}
            </h1>
            <p className="text-[#6B6B76]">
              Monte Carlo simulation for Bitcoin-backed loan assumption
            </p>
          </div>

          {/* Property Summary Card */}
          {property && (
            <Card className="bg-white border-gray-200 p-6 mb-6 shadow-sm">
              <div className="grid md:grid-cols-4 gap-6">
                <div className="md:col-span-2">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-[#E8F4FD] p-2 rounded-lg">
                      <Building2 className="h-6 w-6 text-[#006AFF]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#006AFF] font-medium">{property.property_type?.toUpperCase() || property.type}</div>
                      <div className="text-xl font-bold text-[#2A2A33]">{property.name}</div>
                    </div>
                  </div>
                  <div className="text-sm text-[#6B6B76] mb-2">{property.address}</div>
                  {property.status && (
                    <span className="inline-block text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">{property.status}</span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-[#F7F7F7] rounded-lg p-3">
                    <div className="text-xs text-[#6B6B76]">Property Value</div>
                    <div className="text-lg font-bold text-[#2A2A33]">{fmt(propertyValue)}</div>
                  </div>
                  <div className="bg-[#F7F7F7] rounded-lg p-3">
                    <div className="text-xs text-[#6B6B76]">Current LTV</div>
                    <div className="text-lg font-bold text-[#2A2A33]">{property.ltv_ratio || ltv}%</div>
                  </div>
                  <div className="bg-[#F7F7F7] rounded-lg p-3">
                    <div className="text-xs text-[#6B6B76]">Loan Amount</div>
                    <div className="text-lg font-bold text-[#2A2A33]">{fmt(property.loan_amount || loanAmount)}</div>
                  </div>
                  <div className="bg-[#F7F7F7] rounded-lg p-3">
                    <div className="text-xs text-[#6B6B76]">Occupancy</div>
                    <div className={`text-lg font-bold ${(property.occupancy_rate || 0) < 60 ? 'text-red-600' : 'text-[#2A2A33]'}`}>
                      {property.occupancy_rate || property.occupancy || 'N/A'}%
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-center">
                  <div className="bg-[#E8F4FD] rounded-lg p-4 text-center">
                    <div className="text-sm text-[#6B6B76]">BTC Required at {bitcoinCollateralPct}%</div>
                    <div className="text-2xl font-bold text-[#006AFF]">₿ {btcRequired.toFixed(2)}</div>
                    <div className="text-xs text-[#6B6B76]">${bitcoinCollateral.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Parameters */}
            <div className="space-y-6">
              <Card className="bg-white border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Deal Parameters</h3>
                
                <div className="space-y-5">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">Loan-to-Value (LTV)</label>
                      <span className="text-sm font-semibold text-[#2A2A33]">{ltv}%</span>
                    </div>
                    <Slider value={[ltv]} onValueChange={(val) => setLtv(val[0])} min={20} max={150} step={5} />
                    <div className="text-xs text-[#6B6B76] mt-1">Loan: {fmt(loanAmount)}</div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">Bitcoin Collateral %</label>
                      <span className="text-sm font-semibold text-[#2A2A33]">{bitcoinCollateralPct}% of Loan</span>
                    </div>
                    <Slider value={[bitcoinCollateralPct]} onValueChange={(val) => setBitcoinCollateralPct(val[0])} min={20} max={60} step={1} />
                    <div className="text-xs text-[#6B6B76] mt-1">{fmt(bitcoinCollateral)} ({btcRequired.toFixed(2)} BTC)</div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">BTC Haircut (Risk Adjustment)</label>
                      <span className="text-sm font-semibold text-[#2A2A33]">{aggregateHaircut}%</span>
                    </div>
                    <Slider value={[aggregateHaircut]} onValueChange={(val) => setAggregateHaircut(val[0])} min={30} max={70} step={5} />
                    <div className="text-xs text-[#6B6B76] mt-1">Usable BTC Collateral: {fmt(bitcoinCollateral * (1 - aggregateHaircut / 100))}</div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">Interest Rate</label>
                      <span className="text-sm font-semibold text-[#2A2A33]">{interestRate}% APR</span>
                    </div>
                    <Slider value={[interestRate]} onValueChange={(val) => setInterestRate(val[0])} min={3} max={15} step={0.5} />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">Loan Term</label>
                      <span className="text-sm font-semibold text-[#2A2A33]">{loanTermYears} years</span>
                    </div>
                    <Slider value={[loanTermYears]} onValueChange={(val) => setLoanTermYears(val[0])} min={1} max={10} step={1} />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">Default Probability</label>
                      <span className={`text-sm font-semibold ${defaultProbability > 20 ? 'text-red-600' : 'text-[#2A2A33]'}`}>{defaultProbability}%</span>
                    </div>
                    <Slider value={[defaultProbability]} onValueChange={(val) => setDefaultProbability(val[0])} min={0} max={50} step={1} />
                  </div>
                </div>
              </Card>

              <Card className="bg-white border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Market Assumptions</h3>
                
                <div className="space-y-5">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">BTC Expected Return</label>
                      <span className={`text-sm font-semibold ${btcExpectedReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>{btcExpectedReturn}%</span>
                    </div>
                    <Slider value={[btcExpectedReturn]} onValueChange={(val) => setBtcExpectedReturn(val[0])} min={-50} max={100} step={5} />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">BTC Volatility</label>
                      <span className="text-sm font-semibold text-[#2A2A33]">{btcVolatility}%</span>
                    </div>
                    <Slider value={[btcVolatility]} onValueChange={(val) => setBtcVolatility(val[0])} min={20} max={120} step={5} />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">Property Expected Return</label>
                      <span className={`text-sm font-semibold ${propertyExpectedReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>{propertyExpectedReturn}%</span>
                    </div>
                    <Slider value={[propertyExpectedReturn]} onValueChange={(val) => setPropertyExpectedReturn(val[0])} min={-30} max={30} step={1} />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm text-[#6B6B76]">Simulations</label>
                      <span className="text-sm font-semibold text-[#2A2A33]">{numSimulations.toLocaleString()}</span>
                    </div>
                    <Slider value={[numSimulations]} onValueChange={(val) => setNumSimulations(val[0])} min={100} max={5000} step={100} />
                  </div>
                </div>
              </Card>

              <Button 
                onClick={runMonteCarloSimulation}
                disabled={isSimulating}
                className="w-full bg-[#006AFF] hover:bg-[#0055CC] text-white py-6 text-lg"
                data-testid="run-simulation-btn"
              >
                {isSimulating ? (
                  <><Activity className="mr-2 h-5 w-5 animate-spin" /> Running Simulation...</>
                ) : (
                  <><BarChart3 className="mr-2 h-5 w-5" /> Run Monte Carlo Simulation</>
                )}
              </Button>
            </div>

            {/* Right Column - Results */}
            <div className="space-y-6">
              {results ? (
                <>
                  {/* Coverage Summary */}
                  <Card className="bg-[#006AFF] text-white p-6 shadow-sm">
                    <div className="flex items-center mb-4">
                      <Shield className="h-6 w-6 mr-2" />
                      <h3 className="text-lg font-bold">Collateral Coverage Analysis</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold">{results.meanCoverageRatio?.toFixed(0) || 'N/A'}%</div>
                        <div className="text-sm text-blue-100">Mean Coverage</div>
                      </div>
                      <div className="text-center">
                        <div className="text-3xl font-bold">{results.defaultRate.toFixed(1)}%</div>
                        <div className="text-sm text-blue-100">Default Rate</div>
                      </div>
                      <div className="text-center">
                        <div className={`text-3xl font-bold ${results.btcPriceChange >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {results.btcPriceChange >= 0 ? '+' : ''}{results.btcPriceChange.toFixed(1)}%
                        </div>
                        <div className="text-sm text-blue-100">BTC Price Δ</div>
                      </div>
                    </div>
                  </Card>

                  {/* Bank Performance */}
                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-[#2A2A33] mb-4 flex items-center">
                      <DollarSign className="mr-2 h-5 w-5 text-green-600" />
                      Bank Performance
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="text-sm text-[#6B6B76]">Mean Profit</div>
                        <div className="text-3xl font-bold text-green-600">{fmt(results.bank.meanProfit)}</div>
                        <div className="text-sm text-green-600">ROI: {results.bank.roi.toFixed(1)}%</div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-[#F7F7F7] rounded-lg p-3">
                          <div className="text-xs text-[#6B6B76]">5th Percentile (Worst)</div>
                          <div className="text-lg font-semibold text-[#2A2A33]">{fmt(results.bank.percentile5)}</div>
                        </div>
                        <div className="bg-[#F7F7F7] rounded-lg p-3">
                          <div className="text-xs text-[#6B6B76]">95th Percentile (Best)</div>
                          <div className="text-lg font-semibold text-[#2A2A33]">{fmt(results.bank.percentile95)}</div>
                        </div>
                      </div>

                      <div className={`rounded-lg p-4 ${results.bank.lossProbability < 5 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                        <div className="text-sm text-[#6B6B76]">Bank Loss Probability</div>
                        <div className={`text-2xl font-bold ${results.bank.lossProbability < 5 ? 'text-green-600' : 'text-yellow-600'}`}>
                          {results.bank.lossProbability.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* AssumeRate Performance */}
                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-[#2A2A33] mb-4 flex items-center">
                      <Target className="mr-2 h-5 w-5 text-[#006AFF]" />
                      AssumeRate Performance
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="bg-[#E8F4FD] border border-[#006AFF]/20 rounded-lg p-4">
                        <div className="text-sm text-[#6B6B76]">Mean Profit</div>
                        <div className="text-3xl font-bold text-[#006AFF]">{fmt(results.assumeRate.meanProfit)}</div>
                        <div className="text-sm text-[#006AFF]">Revenue: {fmt(results.assumeRate.meanRevenue)}</div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-[#F7F7F7] rounded-lg p-3">
                          <div className="text-xs text-[#6B6B76]">5th Percentile</div>
                          <div className="text-lg font-semibold text-[#2A2A33]">{fmt(results.assumeRate.percentile5)}</div>
                        </div>
                        <div className="bg-[#F7F7F7] rounded-lg p-3">
                          <div className="text-xs text-[#6B6B76]">95th Percentile</div>
                          <div className="text-lg font-semibold text-[#2A2A33]">{fmt(results.assumeRate.percentile95)}</div>
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Summary Table */}
                  <Card className="bg-white border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-bold text-[#2A2A33] mb-4">Summary Statistics</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="border-b border-gray-200">
                          <tr>
                            <th className="text-left py-2 text-[#6B6B76]">Metric</th>
                            <th className="text-right py-2 text-[#6B6B76]">Bank</th>
                            <th className="text-right py-2 text-[#6B6B76]">AssumeRate</th>
                          </tr>
                        </thead>
                        <tbody className="text-[#2A2A33]">
                          <tr className="border-b border-gray-100">
                            <td className="py-2">Mean Profit</td>
                            <td className="text-right font-semibold">{fmt(results.bank.meanProfit)}</td>
                            <td className="text-right font-semibold">{fmt(results.assumeRate.meanProfit)}</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2">Median Profit</td>
                            <td className="text-right">{fmt(results.bank.medianProfit)}</td>
                            <td className="text-right">{fmt(results.assumeRate.medianProfit)}</td>
                          </tr>
                          <tr>
                            <td className="py-2">Loss Probability</td>
                            <td className="text-right">{results.bank.lossProbability.toFixed(1)}%</td>
                            <td className="text-right">{results.assumeRate.lossProbability.toFixed(1)}%</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </>
              ) : (
                <Card className="bg-white border-gray-200 p-12 text-center shadow-sm">
                  <BarChart3 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-[#6B6B76] text-lg mb-2">No simulation results yet</p>
                  <p className="text-[#9B9BA5] text-sm">Adjust parameters and run the Monte Carlo simulation to analyze this deal</p>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PropertyDealAnalysis;
